import React, { ComponentType, Suspense, lazy, useEffect, useMemo } from 'react';

import { createBrowserRouter, RouterProvider, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { useAppSelector } from 'hooks';
import {
  AccessRoles,
  AnalyticsTabs,
  AlertTypes,
  DeviceTypes,
  GroupManagementTabs,
  BillingTabs,
  PatientTabs,
  UserManagementTabs,
  PDFName,
  QuestionnaireBuilderTabs
} from 'constants/enums';
import store from 'store/storeConfig';
import { useCanMigratePatients, useRole } from 'hooks';
import { PermissionDenied } from 'components/Status';
import { Loading } from 'components/Fetch';
import { OAuth, ForgotPassword } from 'pages';
import { DashboardWrapper } from 'pages/Dashboard/Common';
import { getCurrentGroup, getDisclaimerSize, selectGroups } from 'store/groups/reducer';
import { isOrganization } from 'utils/Groups';
import { PatientSurveySession } from 'pages/Patient';
import { PatientWrapper, ThankYou } from 'pages/Patient/Common';
import { getDisclaimerTopClassName, getDisclaimerHeightClassName } from 'utils/UI';
import { SELF_SIGNUP_ENABLED } from 'constants/selfSignup';
import { dtpSignupHref } from 'utils/loginRouter';

/** code splitting imports, only the main pages */
// Reload once on chunk-load failure (stale-deploy recovery); sessionStorage flag prevents reload loops.
const lazyRetry = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  name: string
) =>
  lazy(async () => {
    const key = `lazy-retry-${name}`;
    const hasRefreshed = sessionStorage.getItem(key) === 'true';
    try {
      const mod = await importFn();
      sessionStorage.removeItem(key);
      return mod;
    } catch (err) {
      if (!hasRefreshed) {
        sessionStorage.setItem(key, 'true');
        window.location.reload();
        return new Promise<never>(() => {});
      }
      throw err;
    }
  });

const Alerts = lazyRetry(() => import('pages/Dashboard/Alerts/Alerts'), 'Alerts');
const Tasks = lazyRetry(() => import('pages/Dashboard/Tasks/PatientsTasks'), 'Tasks');
const Devices = lazyRetry(() => import('pages/Dashboard/Common/Devices/Devices'), 'Devices');
const Billing = lazyRetry(() => import('pages/Dashboard/Billing/Billing'), 'Billing');
const ImportPatients = lazyRetry(
  () => import('pages/Dashboard/PatientMigration/ImportPatients'),
  'ImportPatients'
);
const SalesLedOnboarding = lazyRetry(
  () => import('pages/Dashboard/Onboarding'),
  'SalesLedOnboarding'
);
const GroupManagement = lazyRetry(
  () => import('pages/Dashboard/GroupManagement/GroupManagement'),
  'GroupManagement'
);
const EditCarePlanTemplate = lazyRetry(
  () => import('pages/Dashboard/GroupManagement/EditCarePlanTemplate'),
  'EditCarePlanTemplate'
);
const CreateEditAccountWrapper = lazyRetry(
  () => import('pages/Dashboard/GroupManagement/Tabs/UserManagement/CreateEditAccountWrapper'),
  'CreateEditAccountWrapper'
);
const AddAccountWrapper = lazyRetry(
  () => import('pages/Dashboard/GroupManagement/Tabs/UserManagement/AddAccountWrapper'),
  'AddAccountWrapper'
);
const QuestionnaireBuilder = lazyRetry(
  () => import('pages/Dashboard/GroupManagement/Tabs/Questionnaires/QuestionnaireBuilder'),
  'QuestionnaireBuilder'
);
const SmartEventsBuilder = lazyRetry(
  () => import('pages/Dashboard/GroupManagement/SmartEventsBuilder'),
  'SmartEventsBuilder'
);
const Onboard = lazyRetry(() => import('pages/Dashboard/Onboard/Onboard'), 'Onboard');
const PatientsTabs = lazyRetry(
  () => import('pages/Dashboard/Patients/PatientsTabs'),
  'PatientsTabs'
);
const Patient = lazyRetry(() => import('pages/Dashboard/Patients/Patient/Patient'), 'Patient');
const Device = lazyRetry(() => import('pages/Dashboard/Common/Devices/Device'), 'Device');
const Analytics = lazyRetry(() => import('pages/Dashboard/Analytics/Analytics'), 'Analytics');
const PDFAmbulatoryVitalsReport = lazyRetry(
  () => import('pages/PDF/AmbulatoryVitalsReport'),
  'PDFAmbulatoryVitalsReport'
);
const NotFound = lazyRetry(() => import('pages/Status/404'), 'NotFound');
const SsoError = lazyRetry(() => import('pages/Status/SsoError'), 'SsoError');
const AzureVideoCalling = lazyRetry(
  () => import('components/Azure/AzureVideoCalling'),
  'AzureVideoCalling'
);
const SupportDashboard = lazyRetry(
  () => import('pages/Dashboard/Support/SupportDashboard'),
  'SupportDashboard'
);
const AzureMessagingWrapper = lazyRetry(
  () => import('pages/Patient/Messaging/AzureMessagingWrapper'),
  'AzureMessagingWrapper'
);
const DashboardGate = lazyRetry(
  () => import('pages/Dashboard/Patients/DashboardGate'),
  'DashboardGate'
);
const GoogleHealthLanding = lazyRetry(
  () => import('pages/Callback/GoogleHealthLanding'),
  'GoogleHealthLanding'
);
const GoogleHealthErrorLanding = lazyRetry(
  () => import('pages/Callback/GoogleHealthErrorLanding'),
  'GoogleHealthErrorLanding'
);

const Signup = lazyRetry(() => import('pages/Signup/Signup'), 'Signup');

const getLoginPath = () => {
  const { oauth } = store.getState();
  return oauth.authType && oauth.authType === 'se-idp' ? '/?auth=se-idp' : '/';
};

/**
 * Get the path for the router or the urls
 * @param {Boolean} urlFormat
 * Ex: getPath(false).dashboard.alert()     ->  /dashboard/:gid/alerts/:alertType
 *     getPath().dashboard.alert('medical') ->  /dashboard/all/alerts/medical
 */
export const getPath = (urlFormat = true) => {
  const { groups } = store.getState();
  const gid = !urlFormat ? ':gid' : groups.selectedGid;
  const getTabs = (tabs: any) => {
    const array = Object.keys(tabs) as Array<keyof typeof tabs>;
    return array.map((key) => `${tabs[key]}`).join('|');
  };
  return {
    login: !urlFormat ? '/' : getLoginPath(),
    forgotPassword: '/forgotPassword',
    signup: '/signup',
    signupVerify: (signupId?: string) => `/signup/${signupId || ':signupId'}/verify`,
    notFound: '/404',
    ssoError: '/sso-error',
    googleHealthLanding: '/google-health',
    googleHealthErrorLanding: '/google-health/error',
    videoCall: '/video-call',
    pdf: (page?: PDFName) => `/pdf/${page || '*'}`,
    dashboard: {
      layout: '/dashboard',
      patients: `/dashboard/${gid}/patients`,
      alerts: (alertType?: AlertTypes) => `/dashboard/${gid}/alerts/${alertType || ':alertType'}`,
      tasks: () => `/dashboard/${gid}/tasks`,
      patient: (uid?: string, patientTab?: PatientTabs) =>
        `/dashboard/${gid}/patient/${uid || ':uid'}/${patientTab || ':patientTab'}`,
      devices: (deviceType?: DeviceTypes) =>
        `/dashboard/${gid}/devices/${deviceType || ':deviceType'}`,
      device: (deviceId?: string) => `/dashboard/${gid}/device/${deviceId || ':deviceId'}`,
      onboard: `/dashboard/${gid}/invite`,
      importPatients: `/dashboard/${gid}/import-patients`,
      analytics: (analyticsTab?: AnalyticsTabs) =>
        `/dashboard/${gid}/analytics/${analyticsTab || ':analyticsTab'}`,
      groupManagement: {
        layout: (groupManagementTab?: GroupManagementTabs) =>
          `/dashboard/${gid}/group-management/${groupManagementTab || ':groupManagementTab'}`,
        groupManagementEditCarePlanTemplate: (template_id?: string) =>
          `/dashboard/${gid}/group-management/edit-care-plan/${template_id || ':template_id'}`,
        groupManagementUser: (tab: UserManagementTabs, aid?: string) => {
          const path = `/dashboard/${gid}/group-management/${
            GroupManagementTabs.USER_MANAGEMENT
          }/:tab(${getTabs(UserManagementTabs)})/${
            tab === UserManagementTabs.EDIT ? aid || ':aid' : ''
          }`;
          if (tab === UserManagementTabs.ADD)
            return `/dashboard/${gid}/group-management/${GroupManagementTabs.USER_MANAGEMENT}/${UserManagementTabs.ADD}`;
          if (tab === UserManagementTabs.EDIT)
            return `/dashboard/${gid}/group-management/${GroupManagementTabs.USER_MANAGEMENT}/${
              UserManagementTabs.EDIT
            }/${aid || ':aid'}`;
          return path;
        },
        questionnaireBuilder: (
          builderTab: QuestionnaireBuilderTabs = QuestionnaireBuilderTabs.BUILDER,
          surveyId?: string
        ) => {
          if (
            builderTab === QuestionnaireBuilderTabs.EDIT ||
            builderTab === QuestionnaireBuilderTabs.COPY
          ) {
            return `/dashboard/${gid}/group-management/${
              GroupManagementTabs.QUESTIONNAIRES
            }/${builderTab}/${surveyId || ':surveyId'}`;
          } else {
            return `/dashboard/${gid}/group-management/${GroupManagementTabs.QUESTIONNAIRES}/${builderTab}`;
          }
        },
        createSmartEvent: `/dashboard/${gid}/group-management/${GroupManagementTabs.SMART_EVENTS_LIST}/add`
      },
      support: `/dashboard/${gid}/support`,
      /** SYSOP-only: provisioning a prepaid organisation from an executed contract. */
      salesLedOnboarding: `/dashboard/${gid}/onboard-organization`,
      billing: (billingTab?: BillingTabs) =>
        `/dashboard/${gid}/billing/${billingTab || ':billingTab'}`
    },
    patient: {
      survey: (patientId?: string, surveyId?: string) =>
        `/patient/${patientId || ':patientId'}/survey/${surveyId || ':surveyId'}`,
      thankYou: '/patient/thank-you',
      messaging: '/chat/authorize'
    }
  };
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const disclaimerSize = useAppSelector(getDisclaimerSize);
  const disclaimerClassName = getDisclaimerTopClassName(disclaimerSize);
  const disclaimerHeightClassName = getDisclaimerHeightClassName(disclaimerSize);

  return (
    <div
      className={
        location.pathname.includes('/dashboard')
          ? `pageHeight ${disclaimerClassName} ${disclaimerHeightClassName}`
          : 'vh-100'
      }
    >
      {children}
    </div>
  );
};

const DashboardRouteElement = ({
  component,
  wrapper,
  requiredRoles,
  sectionName,
  orgOnly = false,
  access
}: {
  component: React.ReactElement;
  wrapper: boolean;
  requiredRoles?: AccessRoles[];
  sectionName?: string;
  orgOnly?: boolean;
  /**
   * An access decision already made elsewhere, used instead of `requiredRoles`. `useRole` resolves
   * every role except SYSOP through `groupAdmins`, which is a different mechanism from the flat
   * `oauth.data.roles` that `getUserRoles` reads. A feature gated on the flat roles must not also
   * be gated here, or the two disagree: SUPPORT held globally satisfies the API and the sidebar
   * and matches no `groupAdmins` row at all.
   */
  access?: boolean;
}) => {
  const hasRole = useRole(requiredRoles || []);
  const selectedGroup = useAppSelector(getCurrentGroup);
  const isOrg = (selectedGroup && isOrganization(selectedGroup)) || false;

  const isLoadingAuth = useAppSelector((state) => state.oauth.loading);
  const isLoadingGroups = useAppSelector((state) => state.groups.groupInfo.loading);
  const isLoadingAdmins = useAppSelector((state) => state.groups.groupAdmins.loading);
  const hasAuthData = useAppSelector((state) => !!state.oauth.data?.aid);
  const hasGroupsData = useAppSelector(selectGroups).length > 0;

  const isLoading =
    isLoadingAuth || isLoadingGroups || isLoadingAdmins || !hasAuthData || !hasGroupsData;

  const hasAccess = access ?? (orgOnly ? hasRole && isOrg : hasRole);

  if (wrapper) {
    return (
      <DashboardWrapper
        content={
          isLoading ? (
            <Loading />
          ) : hasAccess ? (
            component
          ) : (
            <PermissionDenied sectionName={sectionName || ''} />
          )
        }
      />
    );
  }
  return component;
};

const PatientRouteElement = ({ component }: { component: React.ReactElement }) => {
  return <PatientWrapper content={component} />;
};

/**
 * Leaves for DTP's signup wizard. `replace`, not `assign`, so Back returns to whatever brought the
 * person here rather than to a route on this host that immediately forwards again.
 */
const SignupAway = () => {
  useEffect(() => {
    const href = dtpSignupHref(getPath(false).signup);
    if (href) {
      window.location.replace(href);
    }
  }, []);
  return null;
};

const RouterWrapper = () => {
  const intl = useIntl();
  // The one predicate for patient migration, shared with the sidebar entry and the API's own
  // @Roles(SYSOP, SUPPORT). Three expressions of one idea is what put ADMIN on this route.
  const canMigratePatients = useCanMigratePatients();

  const globalRoutes = [
    {
      path: getPath(false).login,
      element: <OAuth />
    },
    {
      path: getPath(false).forgotPassword,
      element: <ForgotPassword />
    },
    // Self-serve signup only exists on an environment that has opened it; elsewhere these fall
    // through to notFound rather than rendering a wizard whose every call is 404ed [W1].
    //
    // The front door is the exception: it has no wizard either, but `/signup` there must reach DTP
    // rather than 404, because it is the address marketing hands out and the one people bookmark
    // (design §3.5). Only the entry path forwards - a verification link is always issued by DTP and
    // already carries DTP's host, so it never arrives here.
    ...(SELF_SIGNUP_ENABLED
      ? [
          {
            path: getPath(false).signup,
            element: <Signup />
          },
          {
            path: getPath(false).signupVerify(),
            element: <Signup />
          }
        ]
      : dtpSignupHref(getPath(false).signup)
        ? [
            {
              path: getPath(false).signup,
              element: <SignupAway />
            }
          ]
        : []),
    {
      path: getPath(false).notFound,
      element: <NotFound />
    },
    {
      path: getPath(false).ssoError,
      element: <SsoError />
    },
    {
      path: getPath(false).videoCall,
      element: <AzureVideoCalling />
    },
    {
      path: getPath(false).patient.messaging,
      element: <AzureMessagingWrapper />
    },
    {
      path: getPath(false).googleHealthLanding,
      element: <PatientRouteElement component={<GoogleHealthLanding />} />
    },
    {
      path: getPath(false).googleHealthErrorLanding,
      element: <PatientRouteElement component={<GoogleHealthErrorLanding />} />
    }
  ];

  const dashboardRoutes = [
    {
      path: getPath(false).dashboard.layout,
      element: (
        <DashboardRouteElement
          component={<DashboardGate />}
          wrapper={true}
          requiredRoles={[AccessRoles.ADMIN]}
          sectionName={intl.formatMessage({ id: 'patientsKey' })}
        />
      )
    },
    {
      path: getPath(false).dashboard.patients,
      element: (
        <DashboardRouteElement
          component={<PatientsTabs />}
          wrapper={true}
          requiredRoles={[AccessRoles.ADMIN]}
          sectionName={intl.formatMessage({ id: 'patientsKey' })}
        />
      )
    },
    {
      path: getPath(false).dashboard.tasks(),
      element: (
        <DashboardRouteElement
          component={<Tasks />}
          wrapper={true}
          requiredRoles={[AccessRoles.ADMIN]}
          sectionName={intl.formatMessage({ id: 'tasksKey' })}
        />
      )
    },
    {
      path: getPath(false).dashboard.alerts(),
      element: (
        <DashboardRouteElement
          component={<Alerts />}
          wrapper={true}
          requiredRoles={[AccessRoles.ADMIN]}
          sectionName={intl.formatMessage({ id: 'alertsKey' })}
        />
      )
    },
    {
      path: getPath(false).dashboard.patient(),
      element: (
        <DashboardRouteElement
          component={<Patient />}
          wrapper={true}
          requiredRoles={[AccessRoles.ADMIN]}
          sectionName={intl.formatMessage({ id: 'patientsKey' })}
        />
      )
    },
    {
      path: getPath(false).dashboard.devices(),
      element: (
        <DashboardRouteElement
          component={<Devices />}
          wrapper={true}
          requiredRoles={[AccessRoles.ADMIN, AccessRoles.SYSOP]}
          sectionName={intl.formatMessage({ id: 'devicesKey' })}
        />
      )
    },
    {
      path: getPath(false).dashboard.device(),
      element: (
        <DashboardRouteElement
          component={<Device />}
          wrapper={true}
          requiredRoles={[AccessRoles.ADMIN, AccessRoles.SYSOP]}
          sectionName={intl.formatMessage({ id: 'devicesKey' })}
        />
      )
    },
    {
      path: getPath(false).dashboard.analytics(),
      element: (
        <DashboardRouteElement
          component={<Analytics />}
          wrapper={true}
          requiredRoles={[AccessRoles.ADMIN, AccessRoles.GROUP_MANAGEMENT]}
          sectionName={intl.formatMessage({ id: 'reportsKey' })}
        />
      )
    },
    {
      path: getPath(false).dashboard.onboard,
      element: (
        <DashboardRouteElement
          component={<Onboard />}
          wrapper={true}
          requiredRoles={[AccessRoles.ADMIN]}
          sectionName={intl.formatMessage({ id: 'onboardPatientKey' })}
        />
      )
    },
    {
      path: getPath(false).dashboard.importPatients,
      element: (
        <DashboardRouteElement
          component={<ImportPatients />}
          wrapper={true}
          access={canMigratePatients}
          sectionName={intl.formatMessage({ id: 'migrationImportTitleKey' })}
        />
      )
    },
    {
      path: getPath(false).dashboard.salesLedOnboarding,
      element: (
        <DashboardRouteElement
          component={<SalesLedOnboarding />}
          wrapper={true}
          requiredRoles={[AccessRoles.SYSOP]}
          sectionName={intl.formatMessage({ id: 'onboardingNavKey' })}
        />
      )
    },
    {
      path: getPath(false).dashboard.billing(),
      element: (
        <DashboardRouteElement
          component={<Billing />}
          wrapper={true}
          requiredRoles={[AccessRoles.BILLING, AccessRoles.SYSOP]}
          sectionName={intl.formatMessage({ id: 'billingNavKey' })}
          orgOnly={true}
        />
      )
    },
    {
      path: getPath(false).dashboard.groupManagement.layout(),
      element: (
        <DashboardRouteElement
          component={<GroupManagement />}
          wrapper={true}
          /* AccessRoles.BILLING is admitted for the Documents tab alone. The other tabs are
             gated on group administration inside GroupManagement, in both the tab strip and the
             renderer — this route only decides who may open the page. */
          requiredRoles={[AccessRoles.GROUP_MANAGEMENT, AccessRoles.SYSOP, AccessRoles.BILLING]}
          sectionName={intl.formatMessage({ id: 'adminKey' })}
          orgOnly={true}
        />
      )
    },
    {
      path: getPath(false).dashboard.groupManagement.groupManagementEditCarePlanTemplate(),
      element: (
        <DashboardRouteElement
          component={<EditCarePlanTemplate />}
          wrapper={true}
          requiredRoles={[AccessRoles.GROUP_MANAGEMENT, AccessRoles.SYSOP]}
          sectionName={intl.formatMessage({ id: 'carePlanTemplatesKey' })}
          orgOnly={true}
        />
      )
    },
    {
      path: getPath(false).dashboard.groupManagement.groupManagementUser(UserManagementTabs.ADD),
      element: (
        <DashboardRouteElement
          component={<AddAccountWrapper />}
          wrapper={true}
          requiredRoles={[AccessRoles.GROUP_MANAGEMENT, AccessRoles.SYSOP]}
          sectionName="Users"
          orgOnly={true}
        />
      )
    },
    {
      path: getPath(false).dashboard.groupManagement.groupManagementUser(UserManagementTabs.EDIT),
      element: (
        <DashboardRouteElement
          component={<CreateEditAccountWrapper createAccount={false} />}
          wrapper={true}
          requiredRoles={[AccessRoles.GROUP_MANAGEMENT, AccessRoles.SYSOP]}
          sectionName="Users"
          orgOnly={true}
        />
      )
    },
    {
      path: getPath(false).dashboard.groupManagement.createSmartEvent,
      element: (
        <DashboardRouteElement
          component={<SmartEventsBuilder />}
          wrapper={true}
          requiredRoles={[AccessRoles.GROUP_MANAGEMENT, AccessRoles.SYSOP]}
          sectionName={intl.formatMessage({ id: 'smartEventsKey' })}
          orgOnly={true}
        />
      )
    },
    {
      path: getPath(false).pdf(PDFName.AMBULATORY_VITALS),
      element: <PDFAmbulatoryVitalsReport />
    },
    {
      path: getPath(false).dashboard.groupManagement.questionnaireBuilder(),
      element: (
        <DashboardRouteElement
          component={<QuestionnaireBuilder />}
          wrapper={true}
          requiredRoles={[AccessRoles.GROUP_MANAGEMENT, AccessRoles.SYSOP]}
          sectionName={intl.formatMessage({ id: 'questionnaireBuilderKey' })}
          orgOnly={true}
        />
      )
    },
    {
      path: getPath(false).dashboard.groupManagement.questionnaireBuilder(
        QuestionnaireBuilderTabs.EDIT
      ),
      element: (
        <DashboardRouteElement
          component={<QuestionnaireBuilder />}
          wrapper={true}
          requiredRoles={[AccessRoles.GROUP_MANAGEMENT, AccessRoles.SYSOP]}
          sectionName={intl.formatMessage({ id: 'questionnaireBuilderKey' })}
          orgOnly={true}
        />
      )
    },
    {
      path: getPath(false).dashboard.groupManagement.questionnaireBuilder(
        QuestionnaireBuilderTabs.COPY
      ),
      element: (
        <DashboardRouteElement
          component={<QuestionnaireBuilder isCopy={true} />}
          wrapper={true}
          requiredRoles={[AccessRoles.GROUP_MANAGEMENT, AccessRoles.SYSOP]}
          sectionName={intl.formatMessage({ id: 'questionnaireBuilderKey' })}
          orgOnly={true}
        />
      )
    },
    {
      path: getPath(false).dashboard.support,
      element: (
        <DashboardRouteElement
          component={<SupportDashboard />}
          wrapper={true}
          requiredRoles={[AccessRoles.ADMIN]}
          sectionName={intl.formatMessage({ id: 'supportTabKey' })}
        />
      )
    }
  ];

  const patientRoutes = [
    {
      path: getPath(false).patient.survey(),
      element: <PatientRouteElement component={<PatientSurveySession />} />
    },
    {
      path: getPath(false).patient.thankYou,
      element: <PatientRouteElement component={<ThankYou />} />
    }
  ];

  const routes = [
    ...globalRoutes,
    ...dashboardRoutes,
    ...patientRoutes,
    {
      path: '*',
      element: <NotFound />
    }
  ];

  // A new router instance remounts the whole tree, so it must not be rebuilt on every render:
  // one store subscription in this component would otherwise remount the app on every dispatch.
  const router = useMemo(
    () =>
      createBrowserRouter(
        routes.map((route) => ({
          ...route,
          element: (
            <Suspense fallback={<Loading />}>
              <Layout>{route.element}</Layout>
            </Suspense>
          )
        })),
        {
          basename: import.meta.env.BASE_URL
        }
      ),
    // `routes` is rebuilt every render; these are the only values it is built from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, canMigratePatients]
  );

  return <RouterProvider router={router} />;
};

export const Router = RouterWrapper;
